// Per-user risk score breakdown modal. Surfaces every signal's component
// value, weight, sample-size shrink and contribution
// to the final score. Opened from the user table, user detail header
// and AI-summary cards.
//
// Calculations: renders the server's v2 breakdown (roster `serverFactors`).
// Without it (name drills, client-only scopes) it renders the client mirror
// from riskScore.computeBreakdown(email) through the same view, so both paths
// show the same maths.
import React from 'react';
import { Box, Typography, Dialog, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { computeBreakdown, shrunk } from '../riskScore';

function Bar({ value, color }) {
    return (
        <Box sx={{ height: 6, borderRadius: '3px', bgcolor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, Math.max(0, value))}%`, bgcolor: color, borderRadius: '3px', transition: 'width .4s ease' }} />
        </Box>
    );
}

// v2 signal vocabulary: weights 35/20/20/15/10, each raw signal shrunk toward
// 0.5 by sample size so the contributions reconcile with the base score.
const SERVER_SIGNALS = [
    { id: 'failRate',     label: 'Fail rate',       weight: 35, count: 'sims' },
    { id: 'repeatRate',   label: 'Repeat offender', weight: 20, count: 'sims' },
    { id: 'stageDepth',   label: 'Stage depth',     weight: 20, count: 'sims' },
    { id: 'trainingDebt', label: 'Training debt',   weight: 15, count: 'courses' },
    { id: 'courseLag',    label: 'Course lag',      weight: 10, count: 'courses' },
];
export const BAND_META = {
    Low:      { color: '#66BB6A', bg: 'rgba(102,187,106,.12)' },
    Medium:   { color: '#FFCA28', bg: 'rgba(255,202,40,.12)' },
    High:     { color: '#FF7043', bg: 'rgba(255,112,67,.12)' },
    Critical: { color: '#FF5252', bg: 'rgba(255,82,82,.12)' },
};

// Client breakdown -> the roster's factor shape. A course signal with no
// courses in the window is "no data" (null), as on the server.
function clientFactors(breakdown) {
    const raw = Object.fromEntries(breakdown.signals.map((s) => [s.id, s.component]));
    const courses = breakdown.coursesInWindow > 0;
    return {
        failRate: raw['fail-rate'], repeatRate: raw.repeat, stageDepth: raw['stage-depth'],
        trainingDebt: courses ? raw['training-debt'] : null, courseLag: courses ? raw['course-lag'] : null,
        baseScore: breakdown.baseScore, simsInWindow: breakdown.simsInWindow, coursesSent: breakdown.coursesInWindow,
        finalScore: breakdown.finalScore, band: breakdown.band.name,
    };
}

function clientFactorsFor(email) {
    if (!email) return null;
    const breakdown = computeBreakdown(email);
    return breakdown.insufficient ? null : clientFactors(breakdown);
}

// The score is today's base (the sum of the contributions) rounded to a whole
// number; there is no smoothing (BD-1245).
function Breakdown({ factors }) {
    const band = BAND_META[factors.band] || BAND_META.Medium;
    const simsN = factors.simsInWindow ?? 0;
    const coursesN = factors.coursesSent ?? 0;
    const rows = SERVER_SIGNALS.map((s) => {
        const m = s.count === 'sims' ? simsN : coursesN;
        const component = shrunk(factors[s.id], m);
        return { ...s, raw: factors[s.id], component, contribution: s.weight * component };
    });
    return (
        <>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Risk score</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: band.color }}>
                            {factors.finalScore}
                        </Typography>
                        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 700, bgcolor: band.bg, color: band.color }}>
                            {factors.band}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ height: 36, width: '1px', bgcolor: 'divider' }} />
                <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 0.25 }}>Before rounding</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>{(factors.baseScore ?? 0).toFixed(1)}</Box>
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rows.map((s) => {
                    const componentScore = Math.round(s.component * 100);
                    const colorBand = componentScore >= 76 ? '#FF5252' : componentScore >= 56 ? '#FF7043' : componentScore >= 30 ? '#FFCA28' : '#66BB6A';
                    return (
                        <Box key={s.id}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.6, gap: 2 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>{s.label}</Typography>
                                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'baseline' }}>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Weight {s.weight}%</Typography>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>Score <Box component="span" sx={{ fontWeight: 700, color: colorBand }}>{componentScore}</Box></Typography>
                                </Box>
                            </Box>
                            <Bar value={componentScore} color={colorBand} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.6, alignItems: 'baseline', gap: 2 }}>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                    {s.raw == null
                                        ? 'No data - neutral 0.5 baseline'
                                        : `Measured ${(s.raw * 100).toFixed(0)}%, eased toward 0.5 by sample size (${s.count === 'sims' ? `${simsN} sims` : `${coursesN} courses`})`}
                                </Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.primary' }}>
                                    {s.contribution.toFixed(1)} score pts
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    Sum of contributions: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{rows.reduce((a, x) => a + x.contribution, 0).toFixed(1)}</Box>
                    , rounded to <Box component="span" sx={{ fontWeight: 700, color: band.color }}>{factors.finalScore}</Box>
                </Typography>
            </Box>
        </>
    );
}

export default function RiskBreakdownModal({ open, onClose, email, name, dept, go, serverFactors }) {
    const useServer = serverFactors?.finalScore != null;
    const factors = useServer ? serverFactors : clientFactorsFor(email);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider',
                    borderRadius: '10px',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>Risk score breakdown</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', mt: 0.25 }}>{name || email}</Typography>
                    {dept && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{dept}</Typography>}
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.75 }}>
                        Rolling 180-day (6-month) metric — not re-windowed by any selected date range.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.disabled' }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {factors ? (
                <Breakdown factors={factors} />
            ) : (
                <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                        Insufficient sim data to score this user.
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        A score requires at least one simulation delivered in the last 6 months.
                    </Typography>
                </Box>
            )}

            <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                    size="small"
                    onClick={() => { onClose(); go && go('risk-methodology', 'Risk methodology'); }}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: 11, textTransform: 'none', color: 'secondary.main', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                    How is this calculated?
                </Button>
                <Button size="small" variant="contained" onClick={onClose} sx={{ fontSize: 11, textTransform: 'none' }}>Close</Button>
            </Box>
        </Dialog>
    );
}
